/**
 * Business Partnership Analysis Engine (English)
 * Cross-analysis of two Saju charts · 2026 Bǐngwǔ year synergy
 */
import type { SajuResult } from './sajuCalculations';
import type { DongupRadar, DongupAnalysis } from './dongupEngine';

// ── Constants ────────────────────────────────────────────────────
const EL = [0,0,1,1,2,2,3,3,4,4];
const GEN=[1,2,3,4,0],CTRL=[2,3,4,0,1],GENBY=[4,0,1,2,3],CTRLBY=[3,4,0,1,2];
const BEN=['Zǐ','Chǒu','Yín','Mǎo','Chén','Sì','Wǔ','Wèi','Shēn','Yǒu','Xū','Hài'];
const BCN=['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const SEN=['Jiǎ','Yǐ','Bǐng','Dīng','Wù','Jǐ','Gēng','Xīn','Rén','Guǐ'];
const SCN=['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];

function sip(dayIdx:number,tIdx:number):string{
  const d=EL[dayIdx],t=EL[tIdx],dy=dayIdx%2,ty=tIdx%2;
  if(t===d) return dy===ty?'Companion':'Rob Wealth';
  if(t===GEN[d]) return dy===ty?'Eating God':'Hurting Officer';
  if(t===CTRL[d]) return dy===ty?'Indirect Wealth':'Direct Wealth';
  if(t===CTRLBY[d]) return dy===ty?'7 Killings':'Direct Officer';
  if(t===GENBY[d]) return dy===ty?'Indirect Resource':'Direct Resource';
  return 'Companion';
}

function rels(a:number,b:number):string[]{
  const r:string[]=[];
  if((a+6)%12===b||(b+6)%12===a) r.push('충');
  const yh=[[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]];
  if(yh.some(p=>(p[0]===a&&p[1]===b)||(p[1]===a&&p[0]===b))) r.push('육합');
  const sh=[[2,6,10],[11,3,7],[5,9,1],[8,0,4]];
  if(sh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) r.push('삼합');
  if([[2,3,4],[5,6,7],[8,9,10],[11,0,1]].some(g=>g.includes(a)&&g.includes(b)&&a!==b)) r.push('방합');
  if([[2,5],[5,8],[8,2],[1,10],[10,7],[7,1],[0,3],[3,0]].some(p=>p[0]===a&&p[1]===b)) r.push('형');
  if(a===b&&[4,6,9,11].includes(a)) r.push('자형');
  if([[0,9],[9,0],[6,3],[3,6],[4,1],[1,4],[7,10],[10,7],[2,11],[11,2],[5,8],[8,5]].some(p=>p[0]===a&&p[1]===b)) r.push('파');
  if([[0,7],[7,0],[1,6],[6,1],[2,5],[5,2],[3,4],[4,3],[8,11],[11,8],[9,10],[10,9]].some(p=>p[0]===a&&p[1]===b)) r.push('해');
  return r.length?r:['없음'];
}

function relLabelEn(rList:string[]):string{
  if(rList.includes('삼합')) return 'Triple Harmony (三合)';
  if(rList.includes('육합')) return 'Six Harmony (六合)';
  if(rList.includes('방합')) return 'Directional Harmony (方合)';
  if(rList.includes('충')) return 'Clash (冲)';
  if(rList.includes('형')) return 'Punishment (刑)';
  if(rList.includes('파')) return 'Breach (破)';
  if(rList.includes('해')) return 'Harm (害)';
  return 'Neutral';
}

// Day Master metaphors (English)
const META_EN:{[k:number]:{label:string;obj:string;role:string}}={
  0:{label:'Jiǎmù (甲木)',obj:'a towering pine',role:'Vision & Strategy · Leadership'},
  1:{label:'Yǐmù (乙木)',obj:'resilient vines',role:'Relationship Management · HR · Flexible Operations'},
  2:{label:'Bǐnghuǒ (丙火)',obj:'the blazing sun',role:'External Relations · Marketing · Public Face'},
  3:{label:'Dīnghuǒ (丁火)',obj:'a guiding candle',role:'Detailed Planning · Finance · Core Operations'},
  4:{label:'Wùtǔ (戊土)',obj:'a great mountain',role:'General Management · Stability · Organization'},
  5:{label:'Jǐtǔ (己土)',obj:'fertile farmland',role:'Customer Relations · Internal Management · Detailed Work'},
  6:{label:'Gēngjīn (庚金)',obj:'a sharp axe',role:'Aggressive Sales · Decisions · Structuring'},
  7:{label:'Xīnjīn (辛金)',obj:'a polished jewel',role:'Quality Control · Financial Analysis · Precision Planning'},
  8:{label:'Rénshǔi (壬水)',obj:'a vast ocean',role:'Strategic Planning · External Relations · Investment Judgment'},
  9:{label:'Guǐshǔi (癸水)',obj:'life-giving rain',role:'R&D · Internal Design · Data Analysis'},
};

// Ten God → Business metaphor (English)
function sipToBizEn(sipAB:string,sipBA:string,metaA:{obj:string},metaB:{obj:string}):string{
  if(sipAB==='Indirect Wealth'||sipAB==='Direct Wealth')
    return `The structure shows ${metaA.obj} (Person A) handling ${metaB.obj} (Person B) to create value. In Four Pillars terms, Person A obtains wealth (${sipAB}) from Person B — Person A's energy uses Person B as 'raw material' to produce results.`;
  if(sipBA==='Indirect Wealth'||sipBA==='Direct Wealth')
    return `The structure shows ${metaB.obj} (Person B) handling ${metaA.obj} (Person A) to create value. Person B obtains wealth (${sipBA}) from Person A — Person B's drive uses Person A's abilities as raw material to produce results.`;
  if(sipAB==='7 Killings'||sipAB==='Direct Officer')
    return `Person B (${metaB.obj}) plays the ${sipAB} role for Person A (${metaA.obj}). Person B provides structure and discipline. When Person B sets direction and Person A executes, this becomes an optimal partnership.`;
  if(sipBA==='7 Killings'||sipBA==='Direct Officer')
    return `Person A (${metaA.obj}) plays the ${sipBA} role for Person B (${metaB.obj}). Person A leads and controls the partnership — Person A should take the CEO/final decision-maker position.`;
  if(sipAB==='Indirect Resource'||sipAB==='Direct Resource')
    return `Person B (${metaB.obj}) plays the ${sipAB} role for Person A (${metaA.obj}). This is a nurturing relationship where Person B supports and cultivates Person A. Person B becomes a reliable backer.`;
  if(sipBA==='Indirect Resource'||sipBA==='Direct Resource')
    return `Person A (${metaA.obj}) plays the ${sipBA} role for Person B (${metaB.obj}). Person A's experience and insight help Person B grow and develop.`;
  if(sipAB==='Eating God'||sipAB==='Hurting Officer')
    return `Person A's (${metaA.obj}) energy flows into Person B (${metaB.obj}) as ${sipAB}. Person A's creativity and ideas are expressed to the world through Person B.`;
  return `Both partners share the same elemental energy in a ${sipAB} relationship. Strengths overlap, so both competition and cooperation exist simultaneously. Clear role division determines the success of this partnership.`;
}

// Day Branch → Business meaning (English)
function branchToBizEn(rList:string[],aCN:string,bCN:string,aEN:string,bEN:string):string{
  const combo=`${aCN}${bCN}(${aEN}-${bEN})`;
  if(rList.includes('삼합')||rList.includes('육합'))
    return `Your Day Branches form a **${combo} Harmony**. When two people meet and create a new elemental energy, this is the strongest bonding relationship in Four Pillars. The synergy grows the more you're together, naturally generating wealth.`;
  if(rList.includes('방합'))
    return `Your Day Branches form a **${combo} Directional Harmony**. Energy converges in the same direction, creating powerful solidarity. Maximum synergy comes from starting in the same industry or field.`;
  if(rList.includes('충'))
    return `Your Day Branches form a **${combo} Clash**. A Clash is intense energetic collision. Core values and work styles may be opposites, creating early friction. However, when managed well, a Clash becomes the ultimate partnership where each covers the other's blind spots.`;
  if(rList.includes('형'))
    return `Your Day Branches form a **${combo} Punishment**. Punishment carves and shapes — a relationship where you grow through mutual challenge. For long-term partnership, clear boundaries and mutual respect are essential.`;
  if(rList.includes('파'))
    return `Your Day Branches form a **${combo} Breach**. A Breach creates slow-developing cracks. Outwardly fine, but internal interests can diverge. Document financial and equity relationships clearly from the start.`;
  if(rList.includes('해'))
    return `Your Day Branches form a **${combo} Harm**. Beneath surface cooperation, conflicting interests can emerge. Contracts, not trust alone, must sustain this relationship.`;
  return `Your Day Branches maintain a neutral relationship without direct harmony or clash. No dramatic chemistry, but a stable, workable partnership.`;
}

// 2026 year timing analysis (English)
const YS=2,YB=6;
function yearTimingTextEn(person:SajuResult,who:string):string{
  const ds=person.dayPillar.stemIdx;
  const db=person.dayPillar.branchIdx;
  const sipS=sip(ds,YS);
  const brRels=rels(db,YB);
  const relLbl=relLabelEn(brRels);
  const meta=META_EN[ds];
  
  const isComb=(Math.abs(ds-YS)===5)||(ds===2&&YS===7)||(ds===7&&YS===2)||
    (ds+YS===9&&[2,3,4].includes(Math.min(ds,YS)))||
    ((ds-YS+10)%10===5)||((YS-ds+10)%10===5);
  const combText=isComb?` Especially, your Day Master forms a Stem Combination with 2026's Bǐnghuǒ (丙火), making this an excellent year for major contracts and partnerships to materialize.`:'';
  
  let text=`For ${who}, 2026 brings ${sipS} energy to your ${meta.label} Day Master. `;
  if(sipS==='Rob Wealth'||sipS==='Companion') text+=`Competitive energy overflows and power struggles may arise. However, channeling this intense energy into the partnership creates powerful momentum.`;
  else if(sipS==='Indirect Wealth'||sipS==='Direct Wealth') text+=`Wealth luck operates strongly this year. The principle of locking incoming cash flow into safe assets is essential.`;
  else if(sipS==='7 Killings'||sipS==='Direct Officer') text+=`Strong external pressure comes with official recognition, promotions, and contracts. This is the time to establish clear titles and formal roles.`;
  else if(sipS==='Indirect Resource'||sipS==='Direct Resource') text+=`Benefactors and supporters appear this year. Investing in learning, knowledge, and network expansion yields great results.`;
  else if(sipS==='Eating God') text+=`Talent and creativity explode this year. Express your hidden ideas freely.`;
  else text+=`A dynamic year of new attempts and changes. Focus on energy management.`;
  
  if(!brRels.includes('없음')&&relLbl!=='Neutral') {
    text+=` Also, your Day Branch forms ${relLbl} with the annual Wǔ branch, meaning ${brRels.includes('충')?'major changes are forecast in your current environment.':brRels.includes('삼합')||brRels.includes('육합')?'positive energy is further strengthened.':'unseen variables require attention.'}`;
  }
  text+=combText;
  return text;
}

// Radar score calculation (same logic as KO)
function calcRadarEn(a:SajuResult,b:SajuResult):DongupRadar{
  const sipAB=sip(a.dayPillar.stemIdx,b.dayPillar.stemIdx);
  const sipBA=sip(b.dayPillar.stemIdx,a.dayPillar.stemIdx);
  const dayRelList=rels(a.dayPillar.branchIdx,b.dayPillar.branchIdx);
  const yrRelList=rels(a.yearPillar.branchIdx,b.yearPillar.branchIdx);
  const mthRelList=rels(a.monthPillar.branchIdx,b.monthPillar.branchIdx);
  
  let money=55;
  if(['Indirect Wealth','Direct Wealth'].includes(sipAB)||['Indirect Wealth','Direct Wealth'].includes(sipBA)) money+=30;
  if(dayRelList.includes('삼합')||dayRelList.includes('육합')) money+=15;
  if(dayRelList.includes('충')) money-=10;
  
  let role=60;
  if(['7 Killings','Direct Officer','Indirect Wealth','Direct Wealth'].includes(sipAB)||['7 Killings','Direct Officer','Indirect Wealth','Direct Wealth'].includes(sipBA)) role+=20;
  if(EL[a.dayPillar.stemIdx]!==EL[b.dayPillar.stemIdx]) role+=10;
  
  let comm=60;
  if(['Indirect Resource','Direct Resource','Eating God'].includes(sipAB)||['Indirect Resource','Direct Resource','Eating God'].includes(sipBA)) comm+=20;
  if(mthRelList.includes('삼합')||mthRelList.includes('육합')) comm+=15;
  if(mthRelList.includes('충')) comm-=15;
  
  let values=55;
  if(yrRelList.includes('삼합')||yrRelList.includes('육합')) values+=25;
  if(yrRelList.includes('충')) values-=20;
  if(yrRelList.includes('방합')) values+=15;
  
  let stability=60;
  if(dayRelList.includes('삼합')||dayRelList.includes('육합')||dayRelList.includes('방합')) stability+=20;
  if(dayRelList.includes('충')) stability-=15;
  if(dayRelList.includes('파')||dayRelList.includes('해')) stability-=10;
  if(['Companion','Rob Wealth'].includes(sipAB)) stability-=8;
  
  const cap=(v:number)=>Math.max(25,Math.min(98,v));
  return {money:cap(money),role:cap(role),comm:cap(comm),values:cap(values),stability:cap(stability)};
}

// ── Main English analysis function ───────────────────────────────
export function analyzeDongupEn(a:SajuResult,b:SajuResult,nameA:string,nameB:string):DongupAnalysis{
  const da=a.dayPillar.stemIdx,db=b.dayPillar.stemIdx;
  const ba=a.dayPillar.branchIdx,bb=b.dayPillar.branchIdx;
  const ya=a.yearPillar.branchIdx,yb=b.yearPillar.branchIdx;
  
  const sipAB=sip(da,db);
  const sipBA=sip(db,da);
  const dayRelList=rels(ba,bb);
  const yrRelList=rels(ya,yb);
  const metaA=META_EN[da],metaB=META_EN[db];
  
  const radar=calcRadarEn(a,b);
  const score=Math.round((radar.money+radar.role+radar.comm+radar.values+radar.stability)/5);
  const scoreLabel=score>=85?'S-Class Synergy':score>=75?'Strongly Recommended (A)':score>=65?'Complementary Match (B)':score>=55?'Review Carefully (C)':'High Risk (D)';
  
  const pillarA=`${a.yearPillar.stem}${a.yearPillar.branch} ${a.monthPillar.stem}${a.monthPillar.branch} ${a.dayPillar.stem}${a.dayPillar.branch}${a.hourPillar?` ${a.hourPillar.stem}${a.hourPillar.branch}`:''}`;
  const pillarB=`${b.yearPillar.stem}${b.yearPillar.branch} ${b.monthPillar.stem}${b.monthPillar.branch} ${b.dayPillar.stem}${b.dayPillar.branch}${b.hourPillar?` ${b.hourPillar.stem}${b.hourPillar.branch}`:''}`;
  
  const da_el=EL[da],db_el=EL[db];
  let overallMeta='';
  if(CTRL[da_el]===db_el) overallMeta=`${metaA.obj} (${nameA||'Person A'}) handles ${metaB.obj} (${nameB||'Person B'}) to create results.`;
  else if(CTRL[db_el]===da_el) overallMeta=`${metaB.obj} (${nameB||'Person B'}) provides strong direction to ${metaA.obj} (${nameA||'Person A'}).`;
  else if(GEN[da_el]===db_el) overallMeta=`${metaA.obj} (${nameA||'Person A'})'s energy grows and develops ${metaB.obj} (${nameB||'Person B'}).`;
  else if(GEN[db_el]===da_el) overallMeta=`${metaB.obj} (${nameB||'Person B'})'s energy supports ${metaA.obj} (${nameA||'Person A'}) as a reliable foundation.`;
  else overallMeta=`The two energies meet and stimulate mutual growth.`;
  
  const overallText=sipToBizEn(sipAB,sipBA,metaA,metaB);
  const branchBizText=branchToBizEn(dayRelList,BCN[ba],BCN[bb],BEN[ba],BEN[bb]);
  
  const aIsBoss=sipBA==='7 Killings'||sipBA==='Direct Officer'||sipAB==='Indirect Wealth'||sipAB==='Direct Wealth';
  const bIsBoss=sipAB==='7 Killings'||sipAB==='Direct Officer'||sipBA==='Indirect Wealth'||sipBA==='Direct Wealth';
  const roleA={
    title: aIsBoss?'[CEO / Final Decision Maker / Strategic Lead]':`[${metaA.role}]`,
    desc: aIsBoss
      ?`With ${metaA.label} Day Master, you should hold the actual decision-making authority in this partnership. Business direction, core planning, and final contract approvals are your domain. Regardless of age or experience, in this structure, you must hold the power for results to emerge.`
      :`Leverage your ${metaA.label} nature to handle ${metaA.role}. This position maximizes your strengths.`,
  };
  const roleB={
    title: bIsBoss?'[CEO / Final Decision Maker / Strategic Lead]':`[${metaB.role}]`,
    desc: bIsBoss
      ?`With ${metaB.label} Day Master, you should handle direction-setting and external negotiations as the strategic lead.`
      :`Leverage your ${metaB.label} nature to handle ${metaB.role}. This position maximizes your strengths.`,
  };
  
  const timingA=yearTimingTextEn(a,nameA||'Person A');
  const timingB=yearTimingTextEn(b,nameB||'Person B');
  let combined='';
  if(score>=75) combined='2026 is a year where your combined power creates maximum synergy. Bǐngwǔ\'s intense fire energy elevates both of you — this can be the founding year of aggressive business expansion. However, manage finances conservatively to prevent overheating from rapid growth.';
  else if(score>=60) combined='2026 is a suitable year to take the first steps together. Rather than scaling too quickly, focus on building systems and clarifying roles while establishing your foundation.';
  else combined='2026 brings strong energies that may collide between you both. If starting a partnership, emotional management and clear role boundaries are essential to prevent conflict.';
  
  const risks:Array<{title:string;desc:string}>=[];
  if(['Companion','Rob Wealth'].includes(sipAB)||['Companion','Rob Wealth'].includes(sipBA)){
    risks.push({title:'"Same Element, Watch the Power Struggle"',desc:'Similar Day Master energies naturally create competition for leadership. Establishing clear decision-making structures early is essential for this partnership.'});
  }
  if(dayRelList.includes('충')){
    risks.push({title:'"Day Branch Clash — Manage Style Differences"',desc:`Your Day Branches form ${BCN[ba]}${BCN[bb]} Clash. Work pace, methods, and values may differ significantly. Documenting 'work manuals' and 'meeting processes' can transform clashing energy into productive output.`});
  }
  if(yrRelList.includes('충')){
    risks.push({title:'"Worldview Collision — Talk Honestly from the Start"',desc:`Your Year Branches form ${BCN[ya]}${BCN[yb]} Clash. Fundamental life philosophies may differ. Invest early time in sharing business goals and vision.`});
  }
  if(dayRelList.includes('파')||dayRelList.includes('해')){
    risks.push({title:`"${relLabelEn(dayRelList)} — The Partnership Contract is Your Best Protection"`,desc:'Your Day Branch relationship shows potential for slow-developing fractures. Document financial distribution, decision structures, and equity exit terms clearly from the start.'});
  }
  if(risks.length===0){
    risks.push({title:'"Good Chemistry Still Requires a Contract"',desc:'Never skip the contract just because the charts align well. A contract written when things are good becomes the best solution when they go wrong. Documenting equity structure and roles completes this partnership.'});
  }
  
  return {
    personA:{pillarStr:pillarA,label:`${a.dayPillar.stem}${a.dayPillar.branch} Day Pillar (${metaA.label})`,obj:metaA.obj,role:metaA.role},
    personB:{pillarStr:pillarB,label:`${b.dayPillar.stem}${b.dayPillar.branch} Day Pillar (${metaB.label})`,obj:metaB.obj,role:metaB.role},
    score,scoreLabel,sipAB,sipBA,
    dayBranchRels:dayRelList,dayBranchLabel:relLabelEn(dayRelList),
    yearBranchRels:yrRelList,yearBranchLabel:relLabelEn(yrRelList),
    overallTitle:`"${overallMeta}"`,overallMeta,overallText,branchBizText,
    roleA,roleB,
    timing2026:{a:timingA,b:timingB,combined},
    risks,radar,
  };
}
